import { SUPPORTED_LANGUAGE_CODES } from '../constants';

/**
 * Конвертирует код языка из URL формата в формат локали
 * pt-br → pt-BR, остальные без изменений (нормализует к формату локали)
 * @param urlLang - Код языка из URL (всегда lowercase, например: pt-br, es, ru)
 * @returns Код языка в формате локали (pt-BR, es, ru)
 */
export const urlLangToLocale = (urlLang: string): string => {
  // Приводим к lowercase для нормализации
  const normalized = urlLang.toLowerCase();
  // Специальная обработка для pt-BR
  if (normalized === 'pt-br') {
    return 'pt-BR';
  }
  return normalized;
};

/**
 * Конвертирует код языка из формата локали в URL формат
 * pt-BR → pt-br, все остальные тоже в lowercase
 * @param locale - Код языка в формате локали (pt-BR, es, ru)
 * @returns Код языка в URL формате (всегда lowercase: pt-br, es, ru)
 */
export const localeToUrlLang = (locale: string): string => {
  // Приводим все к lowercase для URL
  const normalized = locale.toLowerCase();
  return normalized;
};

/**
 * Извлекает языковой префикс из URL пути
 * @param path - Путь URL (например: '/es/features/' или '/features/')
 * @returns Код языка или null, если префикса нет
 */
export const extractLanguageFromPath = (path: string): string | null => {
  // Регулярное выражение для языкового префикса: /lang/ или /lang-country/ или /lang (без завершающего слэша)
  // Поддерживаем оба варианта: /es/ и /es
  const langMatch = path.match(/^\/([a-z]{2}(-[A-Z]{2})?)(\/|$)/);

  if (langMatch) {
    const langCode = langMatch[1];
    // Проверяем, что это поддерживаемый язык
    if (SUPPORTED_LANGUAGE_CODES.includes(langCode)) {
      return langCode;
    }
  }

  return null;
};

/**
 * Удаляет языковой префикс из пути
 * @param path - Путь с возможным языковым префиксом
 * @returns Путь без языкового префикса
 */
export const removeLanguagePrefix = (path: string): string => {
  const langCode = extractLanguageFromPath(path);
  if (langCode) {
    const result = path.replace(`/${langCode}`, '') || '/';
    return result;
  }
  return path;
};

/**
 * Добавляет языковой префикс к пути
 * @param path - Путь без префикса
 * @param langCode - Код языка
 * @returns Путь с языковым префиксом (или без, если английский)
 */
export const addLanguagePrefix = (path: string, langCode: string): string => {
  // Английский не имеет префикса
  if (langCode === 'en') {
    return path;
  }

  // Убираем существующий префикс, если есть
  const cleanPath = removeLanguagePrefix(path);

  // Добавляем новый префикс
  return `/${langCode}${cleanPath}`;
};

/**
 * Заменяет языковой префикс в пути на новый
 * @param path - Текущий путь
 * @param newLangCode - Новый код языка
 * @returns Путь с новым языковым префиксом
 */
export const replaceLanguagePrefix = (path: string, newLangCode: string): string => {
  const cleanPath = removeLanguagePrefix(path);
  return addLanguagePrefix(cleanPath, newLangCode);
};
